import { UseQueryResult } from '@tanstack/react-query'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { ESITI } from '../../constants'
import { useQsFilters } from '../../hooks/filters'
import { Intervento, PaginatedDJResponse } from '../../types'
import { calcGiorni, getLabelStatoIntervento, getLabelTipoBilancia, getLabelTipoIntervento } from '../../utils/utils'
import { interventiInitFilters } from './InterventiFilters'

interface InterventoTableInterface {
  id: Intervento['id']
  data_intervento: Intervento['data_intervento']
  data_richiesta: Intervento['data_richiesta']
  tipo_intervento: Intervento['tipo_intervento']
  nome_cliente?: Intervento['nome_cliente']
  numero_prenotazione: Intervento['numero_prenotazione']
  bilancia_data?: Intervento['bilancia_data']
  stato: Intervento['stato']
  esito: Intervento['esito']
  pk_cliente?: Intervento['pk_cliente']
}

type InterventiTableProps = {
  interventi: UseQueryResult<PaginatedDJResponse<InterventoTableInterface>, Error>
}

export function InterventiTable({ interventi }: InterventiTableProps) {
  const { uiFilters, setFilters } = useQsFilters(interventiInitFilters)
  return (
    <StickyTable className="StickyWithFilters" isLoading={interventi.isLoading}>
      <thead className="position-sticky top-0 bg-white">
        <Sorter
          value={uiFilters.ordering}
          onSortChange={(ordering) =>
            setFilters({
              ordering,
            })
          }
        >
          <tr>
            <th>
              <div className="d-flex align-items-center">
                Id
                <SortControl field="pk" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Data
                <SortControl field="data_intervento" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Tipo intervento
                <SortControl field="tipo_intervento" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Nome cliente
                <SortControl field="nome_cliente" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Numero prenotazione
                <SortControl field="numero_prenotazione" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Bilancia
                <SortControl field="bilancia__matricola" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Stato intervento
                <SortControl field="stato" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Esito intervento
                <SortControl field="esito" />
              </div>
            </th>
          </tr>
        </Sorter>
      </thead>
      <tbody>
        {interventi?.data?.count === 0 && (
          <tr>
            <td colSpan={8} className="text-center">
              Nessun intervento trovato
            </td>
          </tr>
        )}
        {interventi?.data?.results?.map((intervento) => (
          <tr key={intervento.id}>
            <td>
              <Link to={`/interventi/${intervento.id}`} className="link-md text-body-secondary">
                {intervento.id}
              </Link>
            </td>
            <td>
              {intervento.data_intervento ? dayjs(intervento.data_intervento).format('DD/MM/YYYY') : ''}

              {intervento.stato === 'programmato' &&
                intervento.data_richiesta &&
                calcGiorni(intervento.data_richiesta) > 0 && (
                  <div className="mt-2">
                    <small>{calcGiorni(intervento.data_richiesta)} giorni</small>
                  </div>
                )}
            </td>
            <td>
              <div
                className={classNames('badge', {
                  'bg-intervento-periodico-light': intervento.tipo_intervento === 'verifica_periodica',
                  'text-intervento-periodico': intervento.tipo_intervento === 'verifica_periodica',
                  'bg-rapporto-taratura-light': intervento.tipo_intervento === 'rapporto_taratura',
                  'text-rapporto-taratura': intervento.tipo_intervento === 'rapporto_taratura',
                })}
              >
                {getLabelTipoIntervento(intervento.tipo_intervento)}
              </div>
            </td>
            <td>
              <Link to={`/clienti/${intervento.pk_cliente}`} className="link-md text-body-secondary">
                {intervento.nome_cliente}
              </Link>
            </td>
            <td>{intervento.numero_prenotazione}</td>
            <td>
              <Link to={`/bilance/${intervento.bilancia_data?.id}`}>
                {intervento.bilancia_data?.matricola}{' '}
                {intervento.bilancia_data?.sigla && `(${intervento.bilancia_data?.sigla})`}
              </Link>{' '}
              <br />
              {getLabelTipoBilancia(intervento.bilancia_data?.tipo_bilancia)}
            </td>
            <td>
              <div
                className={classNames('badge', {
                  'badge-state-success':
                    intervento.stato === 'trasmesso' ||
                    (intervento.stato === 'eseguito' && intervento.tipo_intervento === 'rapporto_taratura'),
                  'badge-state-all':
                    intervento.stato !== 'trasmesso' &&
                    (intervento.stato !== 'eseguito' || intervento.tipo_intervento !== 'rapporto_taratura'),
                })}
              >
                {getLabelStatoIntervento(intervento.stato)}
              </div>
            </td>
            <td>{ESITI.find((s) => s.value === intervento.esito)?.label ?? intervento.esito}</td>
          </tr>
        ))}
      </tbody>
    </StickyTable>
  )
}
